import React, { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { TextInput, Divider, Button, Group, Title, Checkbox, createStyles, Box, Image } from '@mantine/core';
import PartnerLogoDropzone from './PartnerLogoDropzone';
import { notifications } from '@mantine/notifications';
import { savePartner, updatePartner } from '../../services/api.service';
import { IconCheck } from '@tabler/icons-react';

// Custom styles
const useStyles = createStyles((theme) => ({
	imageContainer: {
		border: '1px solid #CCCCCC',
		padding: 3,
		borderRadius: theme.radius.sm,
	}
}));

const PartnerForm = ({ closeModal, isEdit, partner }) => {

	// Hooks
	const [isLoading, toggleLoading] = useState(false);
	const [fileURL, setFileURL] = useState('');
	const [isLogoChanged, toggleIsLogoChanged] = useState(false);
	const { classes } = useStyles();
	const form = useForm({
		initialValues: {
			name: '',
			website: '',
			logo: null,
			active: true
		},
	});

	// Check if edit
	useEffect(() => {
		if (isEdit) form.setValues({
			name: partner.name,
			website: partner.website,
			logo: partner.logo,
			active: partner.active,
		});
		// eslint-disable-next-line
	}, []);

	// Submit form
	const onSubmit = (values) => {

		// Set loader
		toggleLoading(true);

		// Conditions
		if (!isEdit) {

			// Call API
			savePartner(values, function () {

				// Show success
				notifications.show({
					title: 'Partner creado correctamente',
					color: 'green',
					icon: <IconCheck />
				});

				// Close after 1 second
				setTimeout(() => {

					// Close modal
					closeModal();

				}, 1000);

			}, function (error) {

				// Remove preloader
				toggleLoading(false);

				// Show error
				notifications.show({
					title: 'Error',
					message: error?.message?.[0] || 'Error inesperado.',
					color: 'red'
				});

			});

		} else {

			// Call API
			updatePartner(partner.id, values, function () {

				// Show success
				notifications.show({
					title: 'Partner modificado correctamente',
					color: 'green',
					icon: <IconCheck />
				});

				// Close after 1 second
				setTimeout(() => {

					// Close modal
					closeModal();

				}, 1000);

			}, function (error) {

				// Remove preloader
				toggleLoading(false);

				// Show error
				notifications.show({
					title: 'Error',
					message: error?.message?.[0] || 'Error inesperado.',
					color: 'red'
				});

			});

		}

	}

	// Process accepted file
	const onDrop = (file) => {
		form.setFieldValue('logo', file[0]);
		setFileURL(URL.createObjectURL(file[0]));
		toggleIsLogoChanged(true);
	}

	// Reject file and show error
	const onReject = (file) => {
		notifications.show({
			title: 'Error',
			message: 'Este tipo de archivo no está soportado.',
			color: 'red'
		});
	}

	return (
		<>
			{/* Form */}
			<form onSubmit={form.onSubmit(onSubmit)}>
				{/* Title */}
				<Title
					order={5}
					mb="md"
				>
					{isEdit ? 'Modificar' : ''} Información de Partner
				</Title>
				{/* Input Group */}
				<Group grow>
					{/* Name */}
					<TextInput
						required
						label="Nombre"
						placeholder="Nombre del partner"
						value={form.values.name}
						onChange={(event) => form.setFieldValue('name', event.currentTarget.value)}
						{...form.getInputProps('name')}
					/>
					{/* Website */}
					<TextInput
						required
						label="Sitio web"
						placeholder="https://sitio-partner.com"
						value={form.values.website}
						onChange={(event) => form.setFieldValue('website', event.currentTarget.value)}
						{...form.getInputProps('website')}
					/>
				</Group>
				{/* Input Group */}
				<Group
					grow
					mt="lg"
				>
					{/* Active */}
					<Checkbox
						label="Activo"
						checked={form.values.active}
						onChange={(event) => form.setFieldValue('active', event.currentTarget.value)}
						{...form.getInputProps('active')}
					/>
				</Group>
				<Divider
					my="md"
				/>
				{/* Subtitle */}
				<Title
					order={5}
					mb="md"
				>
					Logotipo (255x282)
				</Title>
				{/* Dropzone */}
				<Group>
					{/* Zone */}
					<PartnerLogoDropzone
						onDrop={onDrop}
						onReject={onReject}
					/>
					{/* Preview */}
					{form.values.logo !== null ?
						<Box className={classes.imageContainer}>
							<Image
								withPlaceholder
								src={isEdit && !isLogoChanged ? partner.logo : fileURL}
								imageProps={isEdit && !isLogoChanged ? null : { onLoad: () => URL.revokeObjectURL(fileURL) }}
								height={148}
								width={148}
								radius="sm"
							/>
						</Box>
					: null}
				</Group>
				{/* Buttons */}
				<Group
					position="apart"
					grow={!isEdit}
				>
					{/* Cancel */}
					<Button
						mt="xl"
						color="red"
						onClick={closeModal}
						disabled={isLoading}
						variant="light"
						sx={{ flexGrow: isEdit ? 2 : 1 }}
					>
						Cancelar
					</Button>
					{/* Submit */}
					<Button
						mt="xl"
						type="submit"
						loading={isLoading}
						variant="filled"
						sx={{ flexGrow: isEdit ? 2 : 1 }}
					>
						{isEdit ? 'Guardar Cambios' : 'Crear Partner'}
					</Button>
				</Group>
			</form>
		</>
	);

}

export default PartnerForm;
